<template>
  <div class="finance-vessel-inspection-page">
    <div class="table-container">
      <div class="table-header">
        <h3>Finance Vessel Inspection</h3>
        <div class="header-actions">
          <el-button size="small" type="success" plain @click="loadData">
            <el-icon><Refresh /></el-icon> Refresh
          </el-button>
        </div>
      </div>

      <div class="table-wrapper" style="height:550px; overflow:auto;">
        <el-table
          :data="tableData"
          border
          style="width:100%"
          v-loading="loading"
          row-key="orderNo"
          :row-style="{ height: 'auto' }"
        >
          <!-- No - 固定高度 30px -->
          <el-table-column type="index" label="No" width="40" align="center">
            <template #default="{ row }">
              <div class="fixed-cell-center">{{ row._index }}</div>
            </template>
          </el-table-column>
          
          <!-- Order No - 固定高度 30px -->
          <el-table-column prop="orderNo" label="Order No" width="110" align="center">
            <template #default="{ row }">
              <div class="fixed-cell-center">{{ row.orderNo }}</div>
            </template>
          </el-table-column>
          
          <!-- Customer - 固定高度 30px 左对齐 -->
          <el-table-column prop="customer" label="Customer" width="120">
            <template #default="{ row }">
              <div class="fixed-cell-left">{{ row.customer }}</div>
            </template>
          </el-table-column>
          
          <!-- Vessel - 固定高度 30px 左对齐 -->
          <el-table-column prop="vessel" label="Vessel" width="140">
            <template #default="{ row }">
              <div class="fixed-cell-left">{{ row.vessel }}</div>
            </template>
          </el-table-column>
          
          <!-- Port - 固定高度 30px 左对齐 -->
          <el-table-column prop="port" label="Port" width="110">
            <template #default="{ row }">
              <div class="fixed-cell-left">{{ row.port }}</div>
            </template>
          </el-table-column>
          
          <!-- ETB - 固定高度 30px -->
          <el-table-column prop="etb" label="ETB" width="120" align="center">
            <template #default="{ row }">
              <div class="fixed-cell-center">{{ formatDate(row.etb) }}</div>
            </template>
          </el-table-column>
          
          <!-- ETD - 固定高度 30px -->
          <el-table-column prop="etd" label="ETD" width="120" align="center">
            <template #default="{ row }">
              <div class="fixed-cell-center">{{ formatDate(row.etd) }}</div>
            </template>
          </el-table-column>
          
          <!-- P Window - 固定高度 30px -->
          <el-table-column prop="pWindow" label="P Window" width="120" align="center">
            <template #default="{ row }">
              <div class="fixed-cell-center">{{ formatDateOnly(row.pWindow) }}</div>
            </template>
          </el-table-column>
          
          <!-- OPS Remarks - 固定高度 30px 左对齐 -->
          <el-table-column prop="opsRemarks" label="OPS Remarks" width="150">
            <template #default="{ row }">
              <div class="fixed-cell-left">{{ row.opsRemarks || '-' }}</div>
            </template>
          </el-table-column>
          
          <!-- MC Remarks - 自动调节高度，最小高度更小 -->
          <el-table-column prop="mcRemarks" label="MC Remarks" min-width="200">
            <template #default="{ row }">
              <el-input
                v-model="row.mcRemarks"
                type="textarea"
                size="small"
                placeholder="输入MC备注..."
                :autosize="{ minRows: 1, maxRows: 6 }"
                style="width:100%"
                class="auto-height-textarea"
                @input="handleMcRemarksChange(row)"
                @blur="handleMcRemarksBlur(row)"
              />
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'

// ============ 状态 ============
const loading = ref(false)
const tableData = ref([])
let updateTimer = null

// ============ 工具函数 ============
const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const formatDateOnly = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// ============ API 配置 ============
const getApiBase = () => process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000'

const getHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  }
}

// ============ 从 order_monitoring 获取数据 ============
const fetchData = async () => {
  try {
    const response = await fetch(`${getApiBase()}/api/auth/order-monitoring`, {
      headers: getHeaders()
    })
    if (response.status === 401) {
      ElMessage.error('认证已过期，请重新登录')
      setTimeout(() => window.location.href = '/login', 1500)
      return []
    }
    if (!response.ok) throw new Error('获取数据失败')
    
    const result = await response.json()
    console.log('从 order_monitoring 获取的数据:', result)
    
    const filtered = (result || []).filter(item => 
      item.mc === 'P' || item.mc === 'A'
    )
    console.log('过滤后数据 (MC=P 或 MC=A):', filtered)
    return filtered
  } catch (error) {
    console.error('获取数据错误:', error)
    throw error
  }
}

// ============ 加载数据 ============
const loadData = async () => {
  if (loading.value) return
  loading.value = true
  try {
    const data = await fetchData()
    tableData.value = data.map((item, index) => ({
      _index: index + 1,
      orderNo: item.order_no || '',
      customer: item.customer || '',
      vessel: item.vessel || '',
      port: item.port || '',
      etb: item.etb || '',
      etd: item.etd || '',
      pWindow: item.p_window || '',
      opsRemarks: item.ops_remarks || '',
      mcRemarks: item.mc_remarks || '',
      mc: item.mc || '',
      id: item.id
    }))
    console.log('📋 表格数据加载完成:', tableData.value.length, '条')
    
    // 等待DOM更新后调整所有textarea高度
    await nextTick()
    adjustAllTextareaHeights()
  } catch (error) {
    ElMessage.error('加载数据失败')
  } finally {
    loading.value = false
  }
}

// ============ 调整所有textarea高度 ============
const adjustAllTextareaHeights = () => {
  const textareas = document.querySelectorAll('.auto-height-textarea .el-textarea__inner')
  textareas.forEach(textarea => {
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  })
}

// ============ MC Remarks 编辑保存 ============
const handleMcRemarksChange = (row) => {
  if (updateTimer) {
    clearTimeout(updateTimer)
  }
  updateTimer = setTimeout(() => {
    saveMcRemarks(row)
    updateTimer = null
  }, 600)
}

const handleMcRemarksBlur = (row) => {
  if (updateTimer) {
    clearTimeout(updateTimer)
    updateTimer = null
  }
  saveMcRemarks(row)
}

const saveMcRemarks = async (row) => {
  try {
    console.log('保存 MC Remarks:', row.orderNo, '→', row.mcRemarks)
    
    const response = await fetch(`${getApiBase()}/api/auth/order-monitoring/${row.orderNo}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({
        mc_remarks: row.mcRemarks || null
      })
    })

    if (response.status === 401) {
      ElMessage.error('Authentication has expired. Please log in again.')
      setTimeout(() => window.location.href = '/login', 1500)
      return
    }

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to save')
    }

    console.log('MC Remarks saved successfully')
  } catch (error) {
    console.error('Failed to save MC Remarks:', error)
    ElMessage.error(`Failed to save: ${error.message}`)
  }
}

// ============ 生命周期 ============
onMounted(() => {
  loadData()
})

onBeforeUnmount(() => {
  if (updateTimer) {
    clearTimeout(updateTimer)
    updateTimer = null
  }
})
</script>

<style scoped>
.finance-vessel-inspection-page {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.table-container {
  flex: 1;
  overflow: auto;
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #ebeef5;
}

.table-header h3 {
  margin: 0;
  font-size: 18px;
  color: #303133;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.table-wrapper {
  height: 550px;
  overflow: auto;
}

:deep(.el-table) {
  font-size: 13px;
}

:deep(.el-table .cell) {
  padding: 2px 4px;
}

:deep(.el-table td.el-table__cell) {
  padding: 2px 0 !important;
}

:deep(.el-table__header-wrapper) {
  background: #f5f7fa;
}

:deep(.el-table th.el-table__cell) {
  background-color: #3498db !important;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 11px !important;
  padding: 4px 0 !important;
  height: 30px !important;
}

:deep(.el-table th.el-table__cell .cell) {
  font-size: 11px !important;
  line-height: 20px !important;
  color: #ffffff !important;
}

:deep(.el-table__body-wrapper) {
  overflow-y: auto;
}

/* ✅ 固定高度单元格 - 居中 */
.fixed-cell-center {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  height: 30px;
  min-height: 30px;
  max-height: 30px;
  color: #606266;
  font-weight: 400;
  font-size: 11px;
  background: #f5f7fa;
  border-radius: 3px;
  overflow: hidden;
  word-break: break-word;
  line-height: 1.2;
}

/* ✅ 固定高度单元格 - 左对齐 */
.fixed-cell-left {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 6px;
  height: 30px;
  min-height: 30px;
  max-height: 30px;
  color: #606266;
  font-weight: 400;
  font-size: 11px;
  background: #f5f7fa;
  border-radius: 3px;
  overflow: hidden;
  word-break: break-word;
  line-height: 1.2;
}

/* ✅ MC Remarks 自动调节高度 - 更紧凑 */
.auto-height-textarea :deep(.el-textarea__inner) {
  font-size: 11px !important;
  font-weight: 400 !important;
  padding: 2px 4px !important;
  min-height: 22px !important;
  max-height: 150px !important;
  background: #fafafa;
  text-align: left !important;
  line-height: 1.3;
  overflow-y: auto !important;
  transition: height 0.1s ease;
  border-radius: 2px;
}

.auto-height-textarea :deep(.el-textarea__inner:focus) {
  background: #fff;
  border-color: #409eff;
}

.auto-height-textarea :deep(.el-textarea__inner::-webkit-scrollbar) {
  width: 3px;
}

.auto-height-textarea :deep(.el-textarea__inner::-webkit-scrollbar-track) {
  background: #f1f1f1;
  border-radius: 2px;
}

.auto-height-textarea :deep(.el-textarea__inner::-webkit-scrollbar-thumb) {
  background: #c1c7cd;
  border-radius: 2px;
}

/* 表格行高度自适应 */
:deep(.el-table__row) {
  height: auto !important;
}

:deep(.el-table__row td) {
  height: auto !important;
}

:deep(.el-table__row td .cell) {
  height: auto !important;
  padding: 2px 4px !important;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8b0b8;
}
</style>